import { useSyncExternalStore } from 'react';

type DescopeTheme = 'light' | 'dark';

/**
 * Descope's own theme (project cssTemplate, see descope/README.md § Theme) declares BOTH a
 * light and a dark palette — which one an embedded `<Descope>` flow renders is chosen by the
 * `theme` prop, not detected automatically. `useThemeDetection` (via `routing/ThemeDetection`, rendered once in main.tsx) is the single place
 * that decides light vs dark for the whole app and writes it to `document.documentElement`'s
 * `data-theme` attribute; this hook reads that same attribute (rather than re-deriving the
 * preference) so the login flow always matches whatever the rest of the app is currently
 * showing, including live toggles and `prefers-color-scheme` changes.
 *
 * `useSyncExternalStore` re-reads the attribute after subscribing, so a theme written between
 * this component's render and its subscription (ThemeDetection's effect runs first on a cold
 * load of `/login`) is still picked up.
 */
export const useDescopeTheme = (): DescopeTheme => useSyncExternalStore(subscribe, readTheme, () => 'light');

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  return () => observer.disconnect();
}

function readTheme(): DescopeTheme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}
